import moment from "moment";
import momentTz from "moment-timezone";
import { DEFAULT_TIMEZONE } from "definitions/constants";
import { NewHireJourney } from "models/v2/newHireJourney";
import { StageKey, STAGES_MAP } from "./constants";

export function getStageInfo(key: StageKey): (typeof STAGES_MAP)[StageKey] {
  return STAGES_MAP[key] || {};
}

export function stageKeyCalculator(startDate: Date | undefined, comparedDate: Date | undefined): StageKey | undefined {
  if (!startDate || !comparedDate) {
    return undefined;
  }

  const targetDate = moment(comparedDate);
  const momentStartDate = moment(startDate);

  if (targetDate.isBefore(momentStartDate, "day")) {
    return "preboarding";
  } else if (targetDate.isSame(momentStartDate, "day")) {
    return "day1";
  } else if (targetDate.isBefore(momentStartDate.clone().add(1, "week"), "day")) {
    return "week1";
  } else if (targetDate.isBefore(momentStartDate.clone().add(2, "week"), "day")) {
    return "week2";
  } else if (targetDate.isBefore(momentStartDate.clone().add(1, "month"), "day")) {
    return "month1";
  } else if (targetDate.isBefore(momentStartDate.clone().add(2, "month"), "day")) {
    return "month2";
  } else if (targetDate.isBefore(momentStartDate.clone().add(3, "month"), "day")) {
    return "month3";
  } else if (targetDate.isBefore(momentStartDate.clone().add(6, "month"), "day")) {
    return "afterMonth3";
  } else if (targetDate.isBefore(momentStartDate.clone().add(12, "month"), "day")) {
    return "afterMonth6";
  } else if (targetDate.isSameOrAfter(momentStartDate.clone().add(12, "month"), "day")) {
    return "afterMonth12";
  }
  return undefined;
}

export function newHireJourneyStageInfo(newHireJourney: NewHireJourney | undefined) {
  if (!newHireJourney) {
    return { key: "", label: "Loading..." };
  }
  const timezone = newHireJourney?.newHire?.timezone ?? DEFAULT_TIMEZONE;
  const todayInNewHireTimezone = momentTz().tz(timezone).toDate();
  const startDateInNewHireTimezone = momentTz.tz(newHireJourney?.startDate, timezone).toDate();
  const keyStage = stageKeyCalculator(startDateInNewHireTimezone, todayInNewHireTimezone);
  return getStageInfo(keyStage as any);
}

export function getStageStartDate(
  newHireJourney: NewHireJourney,
  stageName: string,
) {
  const timezone = newHireJourney?.newHire?.timezone ?? DEFAULT_TIMEZONE;
  const start = momentTz.tz(newHireJourney.startDate, timezone);

  switch (stageName) {
    case "preboarding":
      // Preboarding starts when journey is kicked off (pending -> active)
      return newHireJourney.pendingUpdatedAt
        ? momentTz.tz(newHireJourney.pendingUpdatedAt, timezone)
        : start.clone().subtract(30, "days");
    case "day1":
      return start.clone();
    case "week1":
      return start.clone().add(1, "day");
    case "week2":
      return start.clone().add(1, "week");
    case "month1":
      return start.clone().add(2, "weeks");
    case "month2":
      return start.clone().add(1, "month");
    case "month3":
      return start.clone().add(2, "months");
    case "afterMonth3":
      return start.clone().add(3, "months");
    case "afterMonth6":
      return start.clone().add(6, "months");
    case "afterMonth12":
      return start.clone().add(12, "months");
    default:
      return start;
  }
}

export function getStageEndDate(
  newHireJourney: NewHireJourney,
  stageName: string,
) {
  const timezone = newHireJourney?.newHire?.timezone ?? DEFAULT_TIMEZONE;
  const start = momentTz.tz(newHireJourney.startDate, timezone);

  switch (stageName) {
    case "preboarding":
      return start.clone().subtract(1, "day");
    case "day1":
      return start.clone();
    case "week1":
      return start.clone().add(1, "week").subtract(1, "day");
    case "week2":
      return start.clone().add(2, "weeks").subtract(1, "day");
    case "month1":
      return start.clone().add(1, "month").subtract(1, "day");
    case "month2":
      return start.clone().add(2, "months").subtract(1, "day");
    case "month3":
      return start.clone().add(3, "months").subtract(1, "day");
    case "afterMonth3":
      return start.clone().add(6, "months").subtract(1, "day");
    case "afterMonth6":
      return start.clone().add(12, "months").subtract(1, "day");
    case "afterMonth12":
      // Open-ended stage — sentinel that keeps status as "current" indefinitely
      // once entered. Callers rendering a date range should special-case this key.
      return start.clone().add(100, "years");
    default:
      return start;
  }
}

// Helper to find closest business day
function getClosestBusinessDay(date: momentTz.Moment) {
  const day = date.day();
  if (day === 0) return date.clone().add(1, "day"); // Sunday -> Monday
  if (day === 6) return date.clone().subtract(1, "day"); // Saturday -> Friday
  return date;
}

// Helper to get last business day before a date
function getLastBusinessDay(date: momentTz.Moment) {
  let lastDay = date.clone().subtract(1, "day");
  while (lastDay.day() === 0 || lastDay.day() === 6) {
    lastDay = lastDay.subtract(1, "day");
  }
  return lastDay;
}

export function getStageStatus(
  journey: NewHireJourney,
  stageKey: string,
): "complete" | "current" | "upcoming" {
  const timezone = journey?.newHire?.timezone ?? DEFAULT_TIMEZONE;
  const today = momentTz().tz(timezone);
  const endDate = getStageEndDate(journey, stageKey);
  const startDate = getStageStartDate(journey, stageKey);
  if (today.isAfter(endDate, "day")) return "complete";
  if (today.isSameOrAfter(startDate, "day")) return "current";
  return "upcoming";
}

export function getStageMilestoneDate(
  newHireJourney: NewHireJourney,
  stageName: string,
): { name: string; date: momentTz.Moment } | null {
  const timezone = newHireJourney?.newHire?.timezone ?? DEFAULT_TIMEZONE;
  const start = momentTz.tz(newHireJourney.startDate, timezone);

  // Milestone names are calendar markers (Day 30, Month 6), not stage-window labels
  // ("3+ Months" is the stage, "Month 6" is the checkpoint at its end).
  switch (stageName) {
    case "preboarding":
      return { name: "Last Prep Day", date: getLastBusinessDay(start) };
    case "day1":
      return { name: "Start Day", date: start.clone() };
    case "month1":
      return { name: "Day 30", date: getClosestBusinessDay(start.clone().add(30, "days")) };
    case "month2":
      return { name: "Day 60", date: getClosestBusinessDay(start.clone().add(60, "days")) };
    case "month3":
      return { name: "Day 90", date: getClosestBusinessDay(start.clone().add(90, "days")) };
    case "afterMonth3":
      return { name: "Month 6", date: getClosestBusinessDay(start.clone().add(6, "months")) };
    case "afterMonth6":
      return { name: "Month 12", date: getClosestBusinessDay(start.clone().add(12, "months")) };
    case "afterMonth12":
      // Open-ended stage past the 1-year mark: no defined milestone date.
      return null;
    default:
      return null;
  }
}
